import { inferRecordingType } from '../../../../services/MusicService';

/**
 * Add-a-song result ranking — cast recordings first, then soundtracks.
 *
 * WHY THIS EXISTS. Search results are concatenated Discogs → Apple →
 * MusicBrainz, which is arbitrary with respect to authority. Observed live
 * (Anthony, 2026-08-02, searching "on my own"): the Original Broadway Cast
 * Recording of Les Misérables came back FIFTH, under Patti LaBelle, a Teen
 * Beach 2 soundtrack and a hip-hop single. At a musical-theatre studio the cast
 * recording is very nearly always the one wanted, so it should not need
 * scrolling to.
 *
 * WHY THE CATEGORY IS DERIVED RATHER THAN READ. Every result carries a
 * `recordingType`, and trusting it would bucket wrongly, because the three
 * services do not share a vocabulary:
 *
 *   Apple / MusicBrainz → 'Cast Recording' | 'Soundtrack' | 'Workshop/Demo' | 'Album'
 *   Discogs             → FORMAT descriptions, joined: 'LP', 'Album', 'Single', …
 *
 * So a Discogs row that genuinely IS a cast recording never says so, and its
 * literal 'Album' collides by accident with Apple's 'Album'. Deriving from the
 * album TITLE — where the authority actually shows ("Les Misérables (Original
 * Broadway Cast Recording)") — treats all three sources alike. The stored
 * value is still folded in as a second signal, since Apple's is trustworthy
 * when present.
 *
 * SORT, NOT FILTER (Anthony's call, 2026-08-02). Nothing is hidden. If an album
 * is titled plainly — "Les Misérables", no cast wording — it categorises as
 * `other` and simply keeps its place; a filter would make it VANISH and the
 * teacher would never learn it was there. Reordering degrades gracefully in a
 * way that hiding cannot.
 *
 * There is deliberately NO 'cover' category. Cast/soundtrack are derivable from
 * text; "this is a cover" needs a notion of the ORIGINAL that no source gives
 * us. The lullaby-label row that prompted this was catchable only because its
 * album happened to say "Lullaby Versions" — luck, not a rule.
 */

export const CATEGORY = {
	CAST: 'cast',
	SOUNDTRACK: 'soundtrack',
	OTHER: 'other',
};

// Lower sorts first.
const RANK = {
	[CATEGORY.CAST]: 0,
	[CATEGORY.SOUNDTRACK]: 1,
	[CATEGORY.OTHER]: 2,
};

/**
 * 'cast' | 'soundtrack' | 'other' for one normalized result.
 *
 * The album title is the primary signal and the stored `recordingType` the
 * secondary. The song TITLE is deliberately NOT consulted: a song called
 * "Soundtrack of My Life" is not a soundtrack, and that false positive would
 * promote it above the real ones.
 */
export const deriveRecordingCategory = (m) => {
	const fromAlbum = inferRecordingType(m?.album || '');
	const haystack = `${fromAlbum} ${m?.recordingType || ''}`.toLowerCase();
	if (haystack.includes('cast')) return CATEGORY.CAST;
	if (haystack.includes('soundtrack') || haystack.includes('motion picture')) {
		return CATEGORY.SOUNDTRACK;
	}
	return CATEGORY.OTHER;
};

/**
 * Cast recordings first, then soundtracks, then everything else.
 *
 * STABLE within each group — ties keep their original index, so the existing
 * per-source ordering survives inside a bucket and the sort is deterministic.
 * (Array.prototype.sort is spec-stable in modern engines, but the explicit
 * index tiebreak means this does not depend on that.)
 *
 * Returns a NEW array; the input is not mutated, because it is React state.
 */
export const rankOptions = (options = []) =>
	options
		.map((opt, index) => ({ opt, index, rank: RANK[deriveRecordingCategory(opt)] }))
		.sort((a, b) => a.rank - b.rank || a.index - b.index)
		.map((entry) => entry.opt);
